import styled from '@emotion/styled'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useIntegrations } from '~/api/app/integration.api'
import { Fonts } from '~/assets/style/fonts'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useSparkStaffAccess } from '~/hooks/useSparkBidder'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { AccessCard } from '~/components/AccessCard'
import { Link } from '~/components/Link'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { SettingStatusCard } from '~/components/SettingStatusCard'
import { currentScopeState } from '~/state'
import { SUPPORT_MAIL } from '~/utils/constants'
import { SETTINGS_NAV_CONFIG, SETTINGS_NAV_PATH, SettingsAccessLabel } from '~/utils/settings'

const SettingsWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 42px;
  & h2 {
    ${Fonts.H2}
    font-weight: 500;
    margin-bottom: 16px;
  }
`

const SettingsNav = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: stretch;

  & > * {
    flex-basis: 388px;
  }
`

export const SettingsHomePage = () => {
  const currentScope = useAtomValue(currentScopeState)
  const navigate = useNavigate()
  const { data: gamIntegrations } = useIntegrations()
  const user = useAuthenticatedUser()
  const { hasNode, hasNodeAccess } = useNodeStaffAccess()
  const { hasSpark, hasSparkAccess } = useSparkStaffAccess()
  const isAdxActive = !!gamIntegrations?.gamNetworks?.some((network) => network.dataIngestionStatus === 'ACTIVE')
  const isOwner = user.getScopeRole() === 'owner' || user.isAdmin
  const displayRefreshAccess = user.hasFeature('refresh')
  // Ad quality is for owners, and comes with Spark or Node. Each answers for the scope's two toggles, so an admin finds it only where
  // a product is onboarded, not on every scope, and an owner only once user access is on.
  const displayAdQualityAccess = isOwner && (hasSpark || hasNode)
  // Open only because the viewer is staff: no product that unlocks it has user access yet.
  const isAdQualityStaffOnly = displayAdQualityAccess && !hasSparkAccess && !hasNodeAccess
  const integrations = { isAdxActive: isAdxActive, isPrebidActive: !!currentScope.pbjs, prebidVariable: currentScope.pbjs }
  const navigateOnClick = (label: SettingsAccessLabel) => {
    // A card whose destination is not its own name says so in SETTINGS_NAV_PATH; the rest derive it.
    const path =
      SETTINGS_NAV_PATH[label] ??
      label
        .split(/\B(?=[A-Z])/)
        .map((w: string) => w.toLowerCase())
        .join('-')
    navigate(path)
  }
  const teamSettings: SettingsAccessLabel[] = ['profile', 'members', 'apiKeys', ...(isOwner ? (['security'] as SettingsAccessLabel[]) : [])]
  const publishingSettings: SettingsAccessLabel[] = [
    'sites',
    ...(displayRefreshAccess ? (['refreshGroups', 'refreshExceptions'] as SettingsAccessLabel[]) : []),
    ...(displayAdQualityAccess ? (['adQuality'] as SettingsAccessLabel[]) : []),
  ]

  return (
    <SettingsWrapper>
      <div>
        <h2>Team</h2>
        <SettingsNav>
          {teamSettings.map((setting, index) => (
            <AccessCard
              {...SETTINGS_NAV_CONFIG[setting]}
              onClick={() => navigateOnClick(setting)}
              key={index}
              trackingLabel={`common-settings-${normalizeAsTrackingLabel(SETTINGS_NAV_CONFIG[setting].title)}-navigate`}
            />
          ))}
        </SettingsNav>
      </div>
      <div>
        <h2>Publishing settings</h2>
        <SettingsNav>
          {publishingSettings.map((setting, index) => (
            <AccessCard
              {...SETTINGS_NAV_CONFIG[setting]}
              {...(setting === 'adQuality' && isAdQualityStaffOnly ? { badge: 'Staff only' } : {})}
              onClick={() => navigateOnClick(setting)}
              key={index}
              trackingLabel={`common-settings-${normalizeAsTrackingLabel(SETTINGS_NAV_CONFIG[setting].title)}-navigate`}
            />
          ))}
        </SettingsNav>
      </div>
      <div>
        <h2>Integrations</h2>
        <SettingsNav>
          <SettingStatusCard
            title={'AdX'}
            content={
              integrations.isAdxActive ? (
                <div>AdX is integrated.</div>
              ) : (
                <>
                  <div>AdX is not integrated with your account.</div>
                  <div>
                    Contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> to integrate it.
                  </div>
                </>
              )
            }
            icon={'gam'}
            active={integrations.isAdxActive}
          />
          <SettingStatusCard
            title={'Prebid'}
            content={
              <>
                <div>{integrations.isPrebidActive ? `Global variable: ${integrations.prebidVariable}` : 'Prebid is not integrated with your account.'}</div>
                <div>
                  Contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} />{' '}
                  {integrations.isPrebidActive ? 'to edit it.' : 'to integrate it.'}
                </div>
              </>
            }
            icon={'prebid'}
            active={integrations.isPrebidActive}
          />
        </SettingsNav>
      </div>
    </SettingsWrapper>
  )
}
